/**
 * @file The addressable parts of ONE composite ArTable column — the dotted
 * `aggregationFieldPath` positions a derived value may aggregate, each with the
 * sub-field's label. Consumers: `formDeriveTargetUniverse` (the drawer's "Part of
 * the value" select) and `formDeriveLabelResolvers` (the label THIS dotted spelling
 * renders as; a projection's declared column is the other one, minted by
 * `formDeriveColumnSubFields`), so the path a select OFFERS and the path a sentence
 * NAMES are minted by the same code. Pure: the caller owns the registries.
 * Invariants: the path is spelled `fields.<subFieldId>.value`, exactly as
 * `ArColumnPanel` writes it and `computeColumnAggregation` resolves it; a
 * non-composite column and a sub-field whose data type cannot fold both yield
 * nothing, so an unofferable path is also an unnameable one.
 */
import { fieldDataTypes } from '#types';
import type { CompositeFieldType, FieldType } from '#types';
import { extractFieldsFromLayout } from '@/services/editorExtractors';

export type FormDeriveColumnValuePart = {
  /** The path itself, spelled as `ArColumnField.aggregationFieldPath` spells it. */
  path: string;
  /** `<columnTypeId>.<path>`, the key column-label resolution is keyed by. */
  typeId: string;
  label: string;
};

function formDeriveColumnValueParts(
  columnTypeId: string,
  fieldTypes: Record<string, FieldType>,
  compositeFieldTypes: readonly CompositeFieldType[],
): FormDeriveColumnValuePart[] {
  const composite = compositeFieldTypes.find((candidate) => candidate.docId === columnTypeId);
  const structure = composite?.defaultUILayout?.structure;
  if (!structure) return [];
  return extractFieldsFromLayout(structure, fieldTypes).flatMap((subField) => {
    const dataType = subField.fieldDataTypeId
      ? (fieldDataTypes as Record<string, FieldType>)[subField.fieldDataTypeId]
      : undefined;
    if (dataType?.aggregatable !== true || dataType.compound === true) return [];
    const path = `fields.${subField.fieldId}.value`;
    return [{ path, typeId: `${columnTypeId}.${path}`, label: subField.label }];
  });
}

export { formDeriveColumnValueParts };
